import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchJobsThunk,
  cancelJobThunk,
  retryJobThunk,
} from "../../../store/thunks/campaignThunks";
import { setSelectedJob } from "../../../store/slices/campaignSlice";
import JobCard from "../components/JobCard";
import JobDetailDrawer from "../components/JobDetailDrawer";
import Filter from "../../../components/common/Filter";
import EmptyState from "../../../components/common/EmptyState";
import { notifySuccess, notifyError } from "../../../utils/notification";
import {
  BellIcon,
  InformationCircleIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";
import { jobStatusEnum } from "../constants/notificationConstants";

const JobsPage = () => {
  const dispatch = useDispatch();
  const { jobs, loading, pagination } = useSelector(
    (state) => state.notificationCampaigns
  );

  const [statusFilter, setStatusFilter] = useState("");
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showOnboardingTip, setShowOnboardingTip] = useState(false);

  // Check if user is new (has very few jobs)
  useEffect(() => {
    if (jobs.length > 0 && jobs.length <= 3) {
      setShowOnboardingTip(true);
    }
  }, [jobs.length]);

  useEffect(() => {
    dispatch(
      fetchJobsThunk({
        reset: true,
        cursor: null,
        status: statusFilter,
      })
    );
  }, [dispatch, statusFilter]);

  const handleLoadMore = useCallback(async () => {
    if (!pagination.hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      await dispatch(
        fetchJobsThunk({
          cursor: pagination.nextCursor,
          status: statusFilter,
          reset: false,
        })
      ).unwrap();
    } catch (error) {
      notifyError("Failed to load more messages");
    } finally {
      setIsLoadingMore(false);
    }
  }, [dispatch, pagination, statusFilter, isLoadingMore]);

  const handleViewDetails = (job) => {
    setSelectedJobId(job._id);
    dispatch(setSelectedJob(job));
    setIsDetailOpen(true);
  };

  const handleCancelJob = async (jobId) => {
    if (
      window.confirm(
        "Are you sure you want to cancel this message? This action cannot be undone."
      )
    ) {
      try {
        await dispatch(cancelJobThunk(jobId)).unwrap();
        notifySuccess("Message cancelled successfully");
        dispatch(
          fetchJobsThunk({
            reset: true,
            cursor: null,
            status: statusFilter,
          })
        );
      } catch (error) {
        notifyError("Failed to cancel message. Please try again.");
      }
    }
  };

  const handleRetryJob = async (jobId) => {
    try {
      await dispatch(retryJobThunk(jobId)).unwrap();
      notifySuccess("Message queued to send again");
      dispatch(
        fetchJobsThunk({
          reset: true,
          cursor: null,
          status: statusFilter,
        })
      );
    } catch (error) {
      notifyError("Failed to retry message. Please try again.");
    }
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status === "All Messages" ? "" : status);
  };

  // Get user-friendly status options
  const getStatusOptions = () => {
    const statusMap = {
      "All Messages": "All Messages",
      PENDING: "Scheduled",
      PROCESSING: "Sending",
      SENT: "Sent",
      COMPLETED: "Delivered",
      FAILED: "Failed",
      CANCELLED: "Cancelled",
    };

    return [
      "All Messages",
      ...Object.keys(statusMap)
        .slice(1)
        .map((key) => statusMap[key]),
    ];
  };

  // Calculate summary stats
  const getSummaryStats = () => {
    if (!jobs || jobs.length === 0) return null;

    const stats = jobs.reduce(
      (acc, job) => {
        acc.total += job.stats?.total || 0;
        acc.delivered += job.stats?.delivered || 0;
        acc.opened += job.stats?.opened || 0;
        acc.failed += job.stats?.failed || 0;
        return acc;
      },
      { total: 0, delivered: 0, opened: 0, failed: 0 }
    );

    return stats;
  };

  const summaryStats = getSummaryStats();

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100
      ) {
        handleLoadMore();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleLoadMore]);

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-text">Your Messages</h1>
            <p className="text-neutral-dark dark:text-neutral-light mt-1">
              Track and manage all your notification campaigns
            </p>
          </div>
          <Filter
            label="Show"
            options={getStatusOptions()}
            defaultOption="All Messages"
            onSelect={handleStatusFilterChange}
          />
        </div>

        {/* Summary Stats */}
        {summaryStats && summaryStats.total > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-primary-light rounded-lg p-4">
              <div className="flex items-center gap-2">
                <ChartBarIcon className="w-5 h-5 text-text-on-primary" />
                <span className="text-sm font-medium text-text-on-primary">
                  Total Sent
                </span>
              </div>
              <p className="text-2xl font-bold text-text-on-primary mt-1">
                {summaryStats.total.toLocaleString()}
              </p>
            </div>

            <div className="bg-success-light rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-success-dark" />
                <span className="text-sm font-medium text-success-dark">
                  Delivered
                </span>
              </div>
              <p className="text-2xl font-bold text-success-dark mt-1">
                {summaryStats.delivered.toLocaleString()}
              </p>
            </div>

            <div className="bg-secondary-light rounded-lg p-4">
              <div className="flex items-center gap-2">
                <BellIcon className="w-5 h-5 text-secondary-dark" />
                <span className="text-sm font-medium text-secondary-dark">
                  Opened
                </span>
              </div>
              <p className="text-2xl font-bold text-secondary-dark mt-1">
                {summaryStats.opened.toLocaleString()}
              </p>
            </div>

            <div className="bg-danger-light rounded-lg p-4">
              <div className="flex items-center gap-2">
                <ExclamationIcon className="w-5 h-5 text-danger-dark" />
                <span className="text-sm font-medium text-danger-dark">
                  Failed
                </span>
              </div>
              <p className="text-2xl font-bold text-danger-dark mt-1">
                {summaryStats.failed.toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {/* Onboarding Tip */}
        {showOnboardingTip && (
          <div className="bg-glass-light border border-primary rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <InformationCircleIcon className="w-5 h-5 text-on-primary mt-0.5" />
              <div>
                <h3 className="font-medium text-on-primary mb-1">
                  Welcome to your message dashboard!
                </h3>
                <p className="text-sm text-text">
                  Here you can see all your sent messages, track their delivery
                  status, and monitor engagement. Click on any message card to
                  see detailed analytics and recipient information.
                </p>
                <button
                  onClick={() => setShowOnboardingTip(false)}
                  className="text-xs text-primary hover:text-primary-dark mt-2 underline"
                >
                  Got it, don't show this again
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Jobs List */}
      {loading && jobs.length === 0 ? (
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-48 bg-neutral-light dark:bg-neutral-dark rounded-xl animate-pulse"
            />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={BellIcon}
          title="No messages yet"
          description={
            statusFilter && statusFilter !== "All Messages"
              ? `No messages found with status "${statusFilter}"`
              : "Send your first notification to see it appear here"
          }
          action={
            !statusFilter || statusFilter === "All Messages" ? (
              <button
                onClick={() => {
                  /* Navigate to create message */
                }}
                className="text-primary hover:text-primary-dark underline"
              >
                Create Your First Message
              </button>
            ) : (
              <button
                onClick={() => setStatusFilter("")}
                className="text-primary hover:text-primary-dark underline"
              >
                View All Messages
              </button>
            )
          }
        />
      ) : (
        <>
          <div className="space-y-6">
            {jobs.map((job) => (
              <JobCard
                key={job._id}
                job={job}
                onViewDetails={handleViewDetails}
                onCancel={handleCancelJob}
                onRetry={handleRetryJob}
              />
            ))}
          </div>

          {/* Load More Indicator */}
          {isLoadingMore && (
            <div className="flex justify-center py-8">
              <div className="flex items-center gap-2 text-neutral-dark dark:text-neutral-light">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                <span className="text-sm">Loading more messages...</span>
              </div>
            </div>
          )}

          {!pagination.hasMore && jobs.length > 5 && (
            <div className="text-center py-8">
              <p className="text-neutral-dark dark:text-neutral-light text-sm">
                You've seen all your messages
              </p>
            </div>
          )}
        </>
      )}

      {/* Job Detail Drawer */}
      <JobDetailDrawer
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        jobId={selectedJobId}
      />
    </div>
  );
};

export default JobsPage;
